import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../store/actions';
import * as streamActions from '../store/actions/InterviewRecordingActions';
import { Modal, Avatar, notification } from 'antd';
import moment from 'moment';
import { get_service_endpoint } from '../ServiceEndpoints.js';
import { history } from '../index';
import { isCookieExpired, isInterviewDeveloper, isInterviewCandidate } from '../helperFunctions';
import eventEmitter, { EVENT_TYPES } from '../EventEmitterService';
import { networkStatusChecker } from '../Utils/networkStatusChecker';
import { store } from '../index';
import { leaveMeeting } from '../Utils/utils.js';
import { RECONNECTION_TIMEOUT, RECONNECTION_ATTEMPT } from '../const';
import styles from '../containers/Interview/InterviewContainer.scss';

export const useInterviewCommonLogic = (props, additionalMessageHandlers = {}, options = {}) => {
  const { customInactivityCheck, enableReconnection = false } = options;
  const [chat, setChat] = useState([]);
  const [currOption, setCurrOption] = useState('video');
  const [isWSConnected, setIsWSConnected] = useState(false);
  const [connection_status, setConnectionStatus] = useState('');
  const [isScreenShareView, setIsScreenShareView] = useState(false);
  const [curr_note, setCurrNote] = useState('');
  const [inactivityModalVisible, setInactivityModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(120);

  const [modal, contextHolder] = Modal.useModal();

  const dispatch = useDispatch();
  const attendees = useSelector(state => state.interviewRecording.attendees);

  // Deep comparison for attendees to prevent unnecessary re-renders
  const attendeesDeepCompare = useMemo(() => {
    // Create a simplified comparison object that excludes circular references
    const comparisonObj = {};
    Object.keys(attendees || {}).forEach(key => {
      const attendee = attendees[key];
      if (attendee && attendee.tileState) {
        comparisonObj[key] = {
          hasLeft: attendee.hasLeft,
          dropped: attendee.dropped,
          localTile: attendee.tileState.localTile,
          isContent: attendee.tileState.isContent,
          tileId: attendee.tileState.tileId,
          attendeeId: attendee.tileState.attendeeId,
        };
      }
    });
    return JSON.stringify(comparisonObj);
  }, [attendees]);

  const wsRef = useRef();
  const intervalRef = useRef();
  const reconnectionTimerRef = useRef();
  const inactivityIntervalRef = useRef();
  const countdownIntervalRef = useRef();
  const currRef = useRef('video');
  const connectionStatusRef = useRef('');
  const maxAttemptRef = useRef(RECONNECTION_ATTEMPT);

  const urlparams = new URLSearchParams(props.location.search);
  const interview_id = urlparams.get('interview_id');

  useEffect(() => {
    let ep = get_service_endpoint('ws');
    wsRef.current = new WebSocket(`${ep}/collab_editor/?interview_id=${interview_id}`);

    if (!localStorage.getItem('access-token') || isCookieExpired()) {
      history.push(`/interviewinvite?invterview_id=${localStorage.getItem('interview_id')}
                &hash_value=${localStorage.getItem('hash_value')}&role=${localStorage.getItem(
        'role',
      )}
                &company_id=${localStorage.getItem('company_id')}`);
    }

    setupWebSocketHandlers(wsRef.current);

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (intervalRef.current) clearInterval(intervalRef.current);
      setIsWSConnected(false);
    };
  }, []);

  useEffect(() => {
    if (props.endInterview) {
      handleEndInterview();
    }
  }, [props.endInterview]);

  useEffect(() => {
    if (isWSConnected && !networkStatusChecker.isActive()) {
      networkStatusChecker.startMonitoring(store, handleOffline);
    }
  }, [isWSConnected]);

  useEffect(() => {
    if (currOption === 'chat') {
      props.setUnreadCount(0);
    }
  }, [currOption]);

  useEffect(() => {
    connectionStatusRef.current = connection_status;
  }, [connection_status]);

  const sendHeartbeat = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'heartbeat',
          data: {},
        }),
      );
    }
  };

  const setupWebSocketHandlers = ws => {
    ws.onopen = () => {
      setIsWSConnected(true);
      eventEmitter.emit(EVENT_TYPES.RECONNECTED, {
        timestamp: Date.now(),
        method: 'onopen',
      });
      if (!intervalRef.current) intervalRef.current = setInterval(sendHeartbeat, 15000);
      if (reconnectionTimerRef.current) {
        clearInterval(reconnectionTimerRef.current);
      }
    };

    ws.onmessage = event => {
      const message = JSON.parse(event.data);
      if (message.type === 'rcv_heartbeat') recieveHeartBeat(message.data);
      else if (message.type === 'chat') showChatHandler(message.data);
      else if (message.type === 'entry' || message.type === 'exit') showUserStatus(message);
      else if (message.type === 'end_interview') endInterview(message.data);
      else if (additionalMessageHandlers[message.type]) {
        additionalMessageHandlers[message.type](message);
      }
    };

    ws.onclose = event => {
      setIsWSConnected(false);
      if (enableReconnection && event.code === 1006) {
        attemptWebSocketReconnection();
      }
    };

    ws.onerror = error => {
      console.error('WebSocket error:', error);
      setIsWSConnected(false);
    };
  };

  const recieveHeartBeat = data => {
    props.setHeartbeat(data['fire_time']);

    if (connectionStatusRef.current === 'offline') {
      setConnectionStatus('online');
      setIsWSConnected(true);
    }
  };

  const handleOffline = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setConnectionStatus('offline');
    setIsWSConnected(false);

    eventEmitter.emit(EVENT_TYPES.CONNECTION_LOST, {
      timestamp: Date.now(),
      reason: 'network_error',
    });

    if (enableReconnection) {
      startSoftReconnection();
      attemptWebSocketReconnection();
    }
  };

  const startSoftReconnection = () => {
    eventEmitter.emit(EVENT_TYPES.RECONNECTING, {
      timestamp: Date.now(),
      type: 'soft_reconnection',
    });

    const startTime = Date.now();
    reconnectionTimerRef.current = setInterval(() => {
      if (Date.now() - startTime > 50000) {
        clearInterval(reconnectionTimerRef.current);
        reconnectionTimerRef.current = null;

        eventEmitter.emit(EVENT_TYPES.RECONNECTION_FAILED, {
          timestamp: Date.now(),
          reason: 'timeout',
        });
      }
    }, 1000);
  };

  const attemptWebSocketReconnection = () => {
    if (maxAttemptRef.current === 0) {
      return;
    }
    setTimeout(() => {
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ep = get_service_endpoint('ws');
      wsRef.current = new WebSocket(`${ep}/collab_editor/?interview_id=${interview_id}`);

      setupWebSocketHandlers(wsRef.current);
      maxAttemptRef.current = maxAttemptRef.current - 1;
    }, RECONNECTION_TIMEOUT);
  };

  const handleEndInterview = () => {
    props.expireMeeting(props.meeting.id, isInterviewDeveloper(props.participants));
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'end_interview',
          data: {},
        }),
      );
    }
  };

  const setCurrNoteState = note => {
    setCurrNote(note);
  };

  const showChatHandler = data => {
    let chat_data = {
      message: data.message,
      user: props.participants?.find(item => item.email === data.user.email)?.name,
      user_id: data.user.user_id,
      fire_time: moment(data.fire_time).format('YYYY-MM-DD HH:mm:ss'),
    };
    setChat(prevChat => [...prevChat, chat_data]);
    if (data.user.email !== localStorage.getItem('username') && currRef.current !== 'chat') {
      props.setUnreadCount(prev => prev + 1);
    }
  };

  const showUserStatus = message => {
    let user_status = {
      type: message.type,
      user: props.participants?.find(item => item.email === message.data.user.email)?.name,
      email: message.data.user.email,
      user_id: message.data.user.user_id,
    };
    setChat(prevChat => [...prevChat, user_status]);
  };

  const endInterview = data => {
    let user = props.participants?.find(e => e.email === data.email);
    props.setEndInterviewUserDetail({ user: user });
    localStorage.setItem('interviewer_name', user['name']);
    leaveMeeting(props.meetingSession, dispatch);
    networkStatusChecker.stopMonitoring();
    if (isInterviewCandidate(props.participants)) {
      return dispatch(actions.setSubmittedBy('candidate')).then(() =>
        history.push(`/survey/interview/${interview_id}`),
      );
    } else {
      if (curr_note) return props.postNote(curr_note, goToEndInterviewPage);
      return goToEndInterviewPage();
    }
  };

  const goToEndInterviewPage = () => {
    history.push(`/interview/feedback?interview_id=${interview_id}`);

    props
      .endMeeting(isInterviewDeveloper(props.participants))
      .then(() => console.log('meeting ended'));
  };

  const sendMessage = message => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'chat',
          data: {
            message: message,
          },
        }),
      );
    }
  };

  const toggleView = () => {
    if (isScreenShareView) {
      setIsScreenShareView(false);
    } else {
      setIsScreenShareView(true);
    }
  };

  const handleRecoveryModalOk = () => {
    props.closeMediaRecoveryModal();
    props.initScreenStream();
  };

  useEffect(() => {
    currRef.current = currOption;
  }, [currOption]);

  useEffect(() => {
    if (customInactivityCheck) {
      const shouldStartTimer = customInactivityCheck(attendees, props.participants);
      if (shouldStartTimer && !inactivityIntervalRef.current) {
        startInactivityTimer();
      } else if (!shouldStartTimer && inactivityIntervalRef.current) {
        clearInactivityTimer();
      }
    } else {
      // Default logic for non-technical interviews
      if (!isInterviewCandidate(props.participants)) return;

      const attendeesArray = Object.values(attendees);
      const interviewers = attendeesArray.filter(attendee =>
        isAttendeeInterviewer(attendee?.tileState),
      );

      const allInterviewersLeft =
        interviewers.length > 0 &&
        interviewers.every(interviewer => interviewer.hasLeft && !interviewer.dropped);

      if (allInterviewersLeft && !inactivityIntervalRef.current) {
        startInactivityTimer();
      } else if (!allInterviewersLeft && inactivityIntervalRef.current) {
        clearInactivityTimer();
      }
    }
  }, [attendees, props.participants, customInactivityCheck]);

  // Candidate left meeting detection
  useEffect(() => {
    // Only developers should be notified when candidates leave
    if (!isInterviewDeveloper(props.participants)) return;

    const attendeesArray = Object.values(attendees);
    const candidates = attendeesArray.filter(attendee => {
      return !attendee?.tileState?.localTile && !attendee?.tileState?.isContent;
    });

    // Check if any candidate has left the meeting
    const candidateLeft = candidates.some(candidate => candidate.hasLeft && !candidate.dropped);

    if (candidateLeft) {
      notification.warning({
        message: 'Candidate Left Meeting',
        description: 'A candidate has left the meeting. Please check if they need assistance.',
        duration: 4.5,
        placement: 'topRight',
      });
    }
  }, [attendeesDeepCompare]);

  const startInactivityTimer = () => {
    if (inactivityIntervalRef.current) clearInterval(inactivityIntervalRef.current);
    inactivityIntervalRef.current = setTimeout(() => {
      showInactivityModal();
    }, 300000);
  };

  const clearInactivityTimer = () => {
    if (inactivityIntervalRef.current) {
      setInactivityModalVisible(false);
      clearInterval(inactivityIntervalRef.current);
      clearInterval(countdownIntervalRef.current);
      inactivityIntervalRef.current = null;
    }
  };

  const showInactivityModal = () => {
    setInactivityModalVisible(true);
    setCountdown(120);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleDisconnect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleContinue = () => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setInactivityModalVisible(false);
    clearInactivityTimer();
  };

  const handleDisconnect = () => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    setInactivityModalVisible(false);
    clearInactivityTimer();
    handleEndInterview();
  };

  const isAttendeeInterviewer = tileState => {
    if (tileState) {
      return !tileState.localTile && !tileState.isContent;
    }
    return false;
  };

  let participants_status = [];
  if (props.participants && props.participants.length > 0) {
    props.participants.forEach(p => {
      if (p.email !== localStorage.getItem('username')) {
        let u = p.name.split(' ')[0];
        u = u.charAt(0).toUpperCase() + u.slice(1);
        u = u.substring(0, 15);
        participants_status.push(
          <div
            key={p.id}
            style={{ padding: '0.5vw' }}
          >
            <Avatar>{p.name.split(' ')[0].charAt(0).toUpperCase()}</Avatar>
            <span
              className={styles['candidate-status__left-icon']}
              data-active={p.isActive ? 'true' : 'false'}
            ></span>
            <span className={styles['candidate-status__left-text']}>{u}</span>
          </div>,
        );
      }
      return;
    });
  }

  return {
    chat,
    currOption,
    setCurrOption,
    isWSConnected,
    isScreenShareView,
    curr_note,
    setCurrNoteState,
    inactivityModalVisible,
    countdown,
    contextHolder,
    participants_status,
    sendMessage,
    toggleView,
    handleRecoveryModalOk,
    handleContinue,
    handleDisconnect,
    handleEndInterview,
    wsRef,
    interview_id,
  };
};
